import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import {
  createSupplierCategory,
  deleteSupplierCategory,
  listSupplierCategories,
  updateSupplierCategory,
  type SupplierCategory,
} from "@/lib/suppliersApi";

type SupplierCategoryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Only CEO / General Manager may delete; everyone else can still add and rename. */
  canDelete: boolean;
  /** Fired whenever the list changed, so the parent can refresh its filters and badges. */
  onCategoriesChanged: () => void;
};

export const SupplierCategoryDialog = ({
  open,
  onOpenChange,
  canDelete,
  onCategoriesChanged,
}: SupplierCategoryDialogProps) => {
  const { toast } = useToast();
  const [categories, setCategories] = useState<SupplierCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setCategories(await listSupplierCategories());
    } catch (error: any) {
      toast({
        title: "Unable to load categories",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      void load();
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setEditingId(null);
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    try {
      setSaving(true);
      if (editingId) {
        await updateSupplierCategory(editingId, { name: trimmed, description: description.trim() || null });
        toast({ title: "Category updated" });
      } else {
        await createSupplierCategory({ name: trimmed, description: description.trim() || null });
        toast({ title: "Category created" });
      }
      resetForm();
      await load();
      onCategoriesChanged();
    } catch (error: any) {
      toast({
        title: editingId ? "Unable to update category" : "Unable to create category",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category: SupplierCategory) => {
    setEditingId(category.id);
    setName(category.name);
    setDescription(category.description ?? "");
  };

  const handleDelete = async (category: SupplierCategory) => {
    // A category still attached to suppliers is refused by the API. Rather than dead-ending the
    // user, offer the detach-and-delete path explicitly.
    const inUse = category.supplier_count > 0;
    const message = inUse
      ? `"${category.name}" is assigned to ${category.supplier_count} supplier${category.supplier_count === 1 ? "" : "s"}. Remove it from them and delete the category?`
      : `Delete the category "${category.name}"?`;
    if (!window.confirm(message)) return;

    try {
      await deleteSupplierCategory(category.id, inUse);
      toast({ title: "Category deleted" });
      if (editingId === category.id) resetForm();
      await load();
      onCategoriesChanged();
    } catch (error: any) {
      toast({
        title: "Unable to delete category",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Supplier categories</DialogTitle>
          <DialogDescription>
            Categories are maintained by the team. Use them to group suppliers by what they provide.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-[1fr_1.5fr_auto]">
            <div className="space-y-1.5">
              <Label htmlFor="supplier-category-name">Name</Label>
              <Input
                id="supplier-category-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Hotels"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void handleSubmit();
                  }
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="supplier-category-description">Description (optional)</Label>
              <Input
                id="supplier-category-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Hotel chains and individual properties"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button type="button" onClick={() => { void handleSubmit(); }} disabled={!name.trim() || saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editingId ? (
                  <Pencil className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                <span className="ml-2">{editingId ? "Save" : "Add"}</span>
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" size="icon" onClick={resetForm} title="Cancel edit">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="max-h-80 space-y-2 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading categories...
              </div>
            )}
            {!loading && categories.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No categories yet. Add the first one above.
              </p>
            )}
            {!loading && categories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{category.name}</span>
                    <Badge variant="secondary" className="text-[11px]">
                      {category.supplier_count} supplier{category.supplier_count === 1 ? "" : "s"}
                    </Badge>
                  </div>
                  {category.description && (
                    <p className="truncate text-sm text-muted-foreground">{category.description}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button type="button" variant="ghost" size="icon" onClick={() => handleEdit(category)} title="Edit">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {canDelete && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => { void handleDelete(category); }}
                      title="Delete"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierCategoryDialog;
